import { addDays, format } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { Spacing } from '@zep/ui';
import { fDateTimeByCountryCode } from '@zep/utils';
import { usePlanStatusType } from '../hooks';
import { usePlanPaymentFormContext, usePlanPaymentInfoContext } from '../PlanPaymentSection/Providers';
import { PlanTemplateType } from '../types';
import { calculatePlanEndDate, convertStartOfDayISOString, getVatPreview, useConvertOnetimePeriodToText } from '../utils';
export const PlanDurationDetails = () => {
  const {
    watch
  } = usePlanPaymentFormContext();
  const planTemplateType = watch('planTemplateType');
  return <div className="w-full rounded-[10px] border border-[#27262E14] bg-white px-[20px] py-[16px] shadow-[0_1px_4px_0_rgba(42,49,65,0.08)]" data-sentry-component="PlanDurationDetails" data-sentry-source-file="PlanDurationDetails.tsx">
      {planTemplateType === PlanTemplateType.PRODUCT_SUBSCRIPTION ? <SubscriptionPlanDetails /> : <OnetimePlanDetails />}
    </div>;
};
const SubscriptionPlanDetails = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    watch
  } = usePlanPaymentFormContext();
  const {
    isKrPayment,
    selectedPlanInfo,
    planPaymentInfo,
    isUpdate,
    userCountryCode
  } = usePlanPaymentInfoContext();
  const planType = watch('planType');
  const planStatusType = usePlanStatusType({
    planType
  });
  const 플랜시작일 = new Date(convertStartOfDayISOString(isUpdate && planStatusType === 'DOWN_GRADE' ? planPaymentInfo?.subscription?.startDate : undefined));
  const subscriptionPeriod = watch('subscriptionPeriod');
  const currencyString = isKrPayment ? '₩' : '$';
  const addSubscriptionPeriod = isUpdate && planStatusType === 'DOWN_GRADE' ? planPaymentInfo?.subscription?.subscriptionPeriod : Number(subscriptionPeriod);
  return <>
      <div className="flex w-full items-center">
        <span className="text-body-2-m font-[500] text-[#27262EE5]">
          {t('subscription:payment.summarySubscriptionUp.nextPay', '다음 결제일')}
        </span>
        <span className="text-body-2-sb ml-auto font-[600] text-[#27262EE5]">
          {fDateTimeByCountryCode(addDays(플랜시작일, addSubscriptionPeriod || 30), planPaymentInfo?.space.countryCode || 'KR')}
        </span>
      </div>
      <Spacing size={10} data-sentry-element="Spacing" data-sentry-source-file="PlanDurationDetails.tsx" />
      <div className="flex w-full items-center">
        <span className="text-body-2-m font-[500] text-[#27262EE5]">
          {t('subscription:payment.summarySubscriptionUp.cyclePay', '결제 주기')}
        </span>
        <span className="text-body-2-sb ml-auto font-[600] text-[#27262EE5]">
          {t('subscription:payment.summarySubscriptionUp.subscriptionPeriodAutoPay', {
          defaultValues: '{subscriptionPeriod}일마다 자동 결제',
          subscriptionPeriod
        })}
        </span>
      </div>
      <Spacing size={10} data-sentry-element="Spacing" data-sentry-source-file="PlanDurationDetails.tsx" />
      <div className="flex w-full items-center">
        <span className="text-body-2-m font-[500] text-[#27262EE5]">
          {t('subscription:payment.summarySubscriptionUp.nextPayPrice', '다음 결제 금액')}
        </span>
        <span className="text-body-2-sb ml-auto font-[600] text-[#27262EE5]">
          {currencyString + ' '}
          {/* 서버 priceInfo 우선. 구버전 응답이면 기존 preview로 fallback한다. */}
          {(selectedPlanInfo.priceInfo?.finalPrice ?? getVatPreview({
          amount: selectedPlanInfo.salePrice,
          currency: isKrPayment ? 'KRW' : 'USD',
          userCountryCode,
          scenario: 'NEW_SUBSCRIPTION_FIRST_PAYMENT'
        }).finalPrice).toLocaleString()}
        </span>
      </div>
    </>;
};
const OnetimePlanDetails = () => {
  const {
    t
  } = useTranslation(['subscription']);
  const {
    watch
  } = usePlanPaymentFormContext();
  const onetimePeriod = watch('onetimePeriod');
  const onetimeStartDate = watch('onetimeStartDate');
  const {
    convertOnetimePeriodToText
  } = useConvertOnetimePeriodToText();
  return <>
      <div className="flex w-full items-center">
        <span className="text-body-2-m font-[500] text-[#27262EE5]">
          {t('subscription:payment.summaryCommon.duration', '이용 기간')}
        </span>
        <span className="text-body-2-sb ml-auto font-[600] text-[#27262EE5]">
          {convertOnetimePeriodToText(onetimePeriod || '')}
        </span>
      </div>

      {onetimeStartDate && onetimePeriod && <>
          <Spacing size={10} />
          <div className="flex w-full items-center">
            <span className="text-body-2-m font-[500] text-[#27262EE5]">
              {t('subscription:payment.summaryCommon.startDate', '플랜 시작일')}
            </span>
            <span className="text-body-2-sb ml-auto font-[600] text-[#27262EE5]">
              {format(onetimeStartDate, 'yyyy. MM. dd')}
            </span>
          </div>
          <Spacing size={10} />
          <div className="flex w-full items-center">
            <span className="text-body-2-m font-[500] text-[#27262EE5]">
              {t('subscription:payment.summaryCommon.endDate', '만료일')}
            </span>
            <span className="text-body-2-sb ml-auto font-[600] text-[#27262EE5]">
              {format(calculatePlanEndDate(onetimeStartDate, onetimePeriod), 'yyyy. MM. dd')}
            </span>
          </div>
        </>}
    </>;
};